import {createRow, type RowModel, type Table} from '@tanstack/react-table'

import {getVisibleAgentTasks} from '../../../components/table/agent-task-utils'
import type {MemexItemModel} from '../../../models/memex-item-model'
import type {SubIssuesByParentId} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {getRowId} from '../helpers/get-row-id'
import type {Row} from '../helpers/types'

/**
 * Creates nested sub-rows for a given parent row.
 *
 * @param parentRow The parent row for which to get nested sub-rows
 * @param subIssuesByParentId A mapping of parent issue IDs to their corresponding sub-issue items
 * @param table The TanStack Table instance
 * @param rowModel The TanStack Table row model
 * @param options.copilotTasksEnabled When true, creates real sub-rows for agent tasks alongside sub-issues
 * @param visitedParentIds Tracks visited parent content IDs during recursion to prevent infinite loops from circular hierarchies
 *
 * @returns An array of nested sub-rows for the given parent row
 */

export const getSubIssueSubRows = (
  parentRow: Row<MemexItemModel>,
  subIssuesByParentId: SubIssuesByParentId,
  table: Table<MemexItemModel>,
  rowModel: RowModel<MemexItemModel>,
  options?: {copilotTasksEnabled?: boolean},
  visitedParentIds: Set<string | number> = new Set(),
) => {
  const parentContentId = parentRow.original.content.id

  // Defensive guard against circular references in the sub-issues hierarchy.
  // The server prevents circular dependencies at creation time, but this protects
  // against edge cases in cached or stale data.
  if (visitedParentIds.has(parentContentId)) return []
  visitedParentIds.add(parentContentId)

  // Get sub-issue items available from the query cache by the row issue's id.
  const data = subIssuesByParentId[parentContentId]

  const subRows: Array<Row<MemexItemModel>> = []

  // Create real sub-rows for agent tasks when enabled. These are virtual rows
  // not backed by a MemexProjectItem — they reuse the parent's MemexItemModel
  // and are marked with agentAssignmentTaskData so cells know to render them as read-only.
  // Agent tasks are placed before sub-issues in the row order.
  if (options?.copilotTasksEnabled) {
    const agentTasks = getVisibleAgentTasks(parentRow.original)
    for (const [index, task] of agentTasks.entries()) {
      const taskRowId = `${parentRow.id}-agent-task-${task.id ?? index}`
      const taskRow = createRow<MemexItemModel>(
        table,
        taskRowId,
        parentRow.original,
        subRows.length + index,
        parentRow.depth + 1,
        undefined,
        parentRow.id,
      )
      taskRow.agentAssignmentTaskData = {task}
      rowModel.flatRows.push(taskRow)
      rowModel.rowsById[taskRow.id] = taskRow
      subRows.push(taskRow)
    }
  }

  if (data) {
    // Initialize custom subIssueData row model property if not set.
    if (!parentRow.subIssueData) {
      parentRow.subIssueData = {subIssueDepth: 0}
    }

    const {subIssues: items, missingSubIssues} = data
    // Sub-issue depth is separate from row depth to account for grouping.
    // Parent subIssueDepth should be defined at this point since it is set above, this is just to appease typechecking.
    const subIssueDepth = (parentRow.subIssueData?.subIssueDepth ?? -1) + 1
    for (const [index, item] of items.entries()) {
      // Create a row id that is unique within the context of the row's ancestors.
      // Built recursively in the format of `${parentRowId}-${itemId}`.
      const newRowId = getRowId(item, index, {id: parentRow.id})
      const newRow = createRow<MemexItemModel>(
        table,
        newRowId,
        item,
        index,
        parentRow.depth + 1,
        undefined,
        parentRow.id,
      )
      newRow.subIssueData = {
        subIssueDepth,
      }

      rowModel.flatRows.push(newRow)
      // Recursively get sub-rows for the new row.
      newRow.subRows = getSubIssueSubRows(newRow, subIssuesByParentId, table, rowModel, options, visitedParentIds)
      rowModel.rowsById[newRow.id] = newRow

      subRows.push(newRow)
    }

    // If the parent issue has missing sub-issues, add a custom row to represent them.
    // Sub-issues are considered missing if they are not in the project.
    if (missingSubIssues && missingSubIssues.length > 0) {
      const missingRow = createRow<MemexItemModel>(
        table,
        getRowId({id: 'missing'}, subRows.length, {id: parentRow.id}),
        parentRow.original,
        subRows.length,
        parentRow.depth + 1,
        undefined,
        parentRow.id,
      )
      missingRow.subIssueData = {
        subIssueDepth,
        missingSubIssues,
      }
      rowModel.flatRows.push(missingRow)
      rowModel.rowsById[missingRow.id] = missingRow
      subRows.push(missingRow)
    }
  }

  return subRows
}
